/**
 * useWorkspaceProvisioning — one-time, on-login workspace bootstrap.
 *
 * On the first authenticated render with NO active room selected, it:
 *   1. loads the user's rooms, and if any exist (preferring rooms the user
 *      created or is a member of), selects the most recent as the active room;
 *   2. otherwise runs the FIRST-RUN flow: seeds the default templates (B+C+W
 *      estimation + Task sizing) if absent, creates a default room for the user
 *      wired to the default estimation template, selects it, and navigates to
 *      it so the user lands in a ready-to-use workspace.
 *
 * Strict layering: all data access goes through the {@link RoomStore} +
 * {@link TemplateStore} (facade only). It runs at most once per session (guarded
 * by a ref) and is entirely best-effort — any failure leaves the app usable and
 * simply skips auto-selection.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useActiveRoom } from './RoomContext';
import { RoomStore } from './roomStore';
import { isRoomMember, roomsAccessibleTo, sortRoomsByRecency } from './roomModel';
import { TemplateStore } from '../templates/templateStore';
import {
  DEFAULT_ESTIMATION_TEMPLATE_NAME,
  seedDefaultTemplates,
} from '../templates/defaultTemplates';

export interface WorkspaceProvisioningState {
  /** True while the one-time provisioning is running. */
  provisioning: boolean;
  /** Set once provisioning has completed (this session). */
  done: boolean;
  /** True when a brand-new default room was created (first run). */
  firstRun: boolean;
}

export function useWorkspaceProvisioning(): WorkspaceProvisioningState {
  const api = useApi();
  const { status, identity } = useAuth();
  const { activeRoomId, setActiveRoomId } = useActiveRoom();
  const navigate = useNavigate();

  const [state, setState] = useState<WorkspaceProvisioningState>({
    provisioning: false,
    done: false,
    firstRun: false,
  });

  // Runs at most once per session.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    if (status !== 'authenticated') return;
    startedRef.current = true;

    const rooms = new RoomStore(api);
    const templates = new TemplateStore(api);

    const run = async () => {
      setState((s) => ({ ...s, provisioning: true }));
      try {
        const alias = identity?.alias ?? (await api.identityProvider.getIdentity()).alias;
        const existing = await rooms.listSummaries();

        // Persisted-active-room self-heal: keep the persisted room ONLY if it
        // still exists AND the signed-in user is a member of it (creator or in
        // memberAliases). If it was deleted/hidden, or the user is no longer a
        // member, fall through and re-select a valid room instead of stranding
        // the user in a room they don't belong to.
        const persisted = activeRoomId
          ? existing.find((r) => r.id === activeRoomId)
          : undefined;
        if (persisted && isRoomMember(persisted, alias)) {
          setState({ provisioning: false, done: true, firstRun: false });
          return;
        }

        if (existing.length > 0) {
          // Prefer rooms the user is a member of (creator OR in memberAliases),
          // using the pointer's mirrored aliases so no S3 hydration is needed,
          // and choose the MOST RECENT such room. SAFE FALLBACK: if the user is
          // a member of none, fall back to the full list (most recent) so they
          // are never locked out of an empty app.
          const accessible = roomsAccessibleTo(existing, alias);
          const pool = accessible.length > 0 ? accessible : existing;
          const chosen = sortRoomsByRecency(pool)[0];
          setActiveRoomId(chosen.id);
          setState({ provisioning: false, done: true, firstRun: false });
          return;
        }

        // First run: seed default templates + create a default room.
        await seedDefaultTemplates(templates);
        const all = await templates.listSummaries();
        const est = all
          .filter((t) => t.name === DEFAULT_ESTIMATION_TEMPLATE_NAME)
          .sort((a, b) => b.version - a.version)[0];
        const room = await rooms.create({
          name: `${identity?.name || alias}'s Room`,
          description: 'Your default workspace, created on first sign-in.',
          defaultTemplateId: est?.id,
        });
        setActiveRoomId(room.id);
        setState({ provisioning: false, done: true, firstRun: true });
        navigate(`/rooms/${encodeURIComponent(room.id)}`);
      } catch {
        // Non-fatal: leave the app usable with no auto-selected room.
        setState({ provisioning: false, done: true, firstRun: false });
      }
    };

    void run();
  }, [status, activeRoomId, identity, api, setActiveRoomId, navigate]);

  return state;
}
